import React, { useState, useEffect, useMemo, useRef } from 'react';
import { MapContainer, ImageOverlay, Polyline, Polygon, Tooltip, useMap } from 'react-leaflet';
import { CRS, latLngBounds } from 'leaflet';
import SpacesService from '../../api/spaces.service';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import Switch from 'devextreme-react/switch';
import { useTheme } from 'contexts/theme';

const DEFAULT_FLOORPLAN = 'https://via.placeholder.com/1000x1000?text=No+Floorplan+Available';

function MapController({ bounds }) {
    const map = useMap();

    useEffect(() => {
        // Ensure map exists and has the fitBounds method
        if (map && bounds && typeof map.fitBounds === 'function') {
            try {
                map.fitBounds(bounds);
            } catch (e) {
                console.error("Leaflet fitBounds error:", e);
            }
        }
    }, [bounds, map]);

    return null;
}

export default function SimpleFloorplan({ spaceId, coordinates, hoveredCoordinates, assetColorMap = {} }) {
    const [floorplanUrl, setFloorplanUrl] = useState(null);
    const [bounds, setBounds] = useState(null);
    const [zones, setZones] = useState([]);
    const [show1m, setShow1m] = useState(false);
    const [show5m, setShow5m] = useState(false);

    // Clean handler functions
    const toggleGrid1 = (e) => setShow1m(e.value);
    const toggleGrid5 = (e) => setShow5m(e.value);

    const { theme } = useTheme();
    // Fallback for an asset missing from assetColorMap: light grey in dark mode
    // so it stays visible, black in light mode (the previous behaviour).
    const fallbackColor = theme === 'dark' ? 'rgb(200,200,200)' : 'rgb(0,0,0)';

    const { processedPaths, processedMarkers, assetLegend } = useMemo(() => {
        console.log("test2")
        console.log('assetColorMap:', assetColorMap);
        console.log("test")
        console.log('coordinates:', coordinates);
        if (!coordinates || !Array.isArray(coordinates) || !assetColorMap || Object.keys(assetColorMap).length === 0) {
            return { processedPaths: [], processedMarkers: [], assetLegend: [] };
        }

        console.log('assetColorMap keys:', Object.keys(assetColorMap));
        console.log('first coordinate asset value:', coordinates[0]?.asset);

        const paths = {};

        const markers = coordinates
            .filter(item => item.spaceCoordinates && typeof item.spaceCoordinates.x === 'number')
            .sort((a, b) => new Date(a.serialNumber) - new Date(b.serialNumber))
            .map((item, index) => {
                const color = assetColorMap[item.asset] ?? fallbackColor;
                const pos = [item.spaceCoordinates.y, item.spaceCoordinates.x];

                if (!paths[item.asset]) {
                    paths[item.asset] = { name: item.asset, color, points: [] };
                }
                paths[item.asset].points.push(pos);

                return { ...item, pos, color, key: `${item.id}-${index}` };
            });

        const assetGroups = Object.values(paths).map(path => ({
            ...path,
            startPoint: path.points[0],
            endPoint: path.points[path.points.length - 1]
        }));

        return {
            processedPaths: assetGroups,
            processedMarkers: markers,
            assetLegend: assetGroups.map(p => ({ name: p.name, color: p.color }))
        };
    }, [coordinates, assetColorMap, fallbackColor]);

    useEffect(() => {
        if (!spaceId) return;
        SpacesService.getSpace(spaceId).then((res) => {
            const spaceData = res.data.data;

            // Image fetch with fallback logic
            SpacesService.getSpaceFloorplan(spaceId).then((imgRes) => {
                if (imgRes?.data && (imgRes.data instanceof Blob || imgRes.data.size > 0)) {
                    const blob = imgRes.data instanceof Blob ? imgRes.data : new Blob([imgRes.data]);
                    setFloorplanUrl(URL.createObjectURL(blob));
                } else {
                    setFloorplanUrl(DEFAULT_FLOORPLAN);
                }
            }).catch(() => {
                setFloorplanUrl(DEFAULT_FLOORPLAN);
            });

            const refCoords = spaceData.localCoordinates;
            if (refCoords && refCoords.length > 0) {
                let xMax = 0, yMax = 0;
                refCoords.forEach(([x, y]) => {
                    if (x > xMax) xMax = x; if (y > yMax) yMax = y;
                });
                setBounds(latLngBounds([0, 0], [yMax, xMax]));
            } else {
                // Bounds fallback so the map doesn't get stuck in loading
                setBounds(latLngBounds([0, 0], [1000, 1000]));
            }

            const floorId = spaceData.referenceSpace?.id || spaceId;
            SpacesService.getChildZones(floorId).then((zoneRes) => {
                const fetchedZones = (zoneRes.data.data || [])
                    .filter(z => z.localCoordinates && z.id !== spaceId)
                    .map(z => ({
                        id: z.id,
                        name: z.name,
                        coords: z.localCoordinates.map(([x, y]) => [y, x])
                    }));
                setZones(fetchedZones);
            });
        });
    }, [spaceId]);

    const hoverPoint = (hoveredCoordinates && typeof hoveredCoordinates.x === 'number')
        ? [hoveredCoordinates.y, hoveredCoordinates.x] : null;

    if (!bounds || !floorplanUrl) return <div style={{ padding: '20px' }}>Loading...</div>;

    // This component bypasses the problematic React-Leaflet CircleMarker wrapper
    function SafeCircleMarker({ center, radius, pathOptions, tooltip }) {
        const map = useMap();
        const markerRef = useRef(null);

        useEffect(() => {
            if (!map || !center) return;

            const marker = L.circleMarker(center, {
                radius,
                ...pathOptions
            });

            if (tooltip) {
                marker.bindTooltip(tooltip);
            }

            marker.addTo(map);
            markerRef.current = marker;

            return () => {
                marker.remove();
            };
        }, [map, center]);

        return null;
    }

    function GridLines({ bounds, spacing, show, color = "#b60703", opacity = 1 }) {
        if (!show || !bounds) return null;

        const latMax = bounds.getNorth();
        const latMin = bounds.getSouth();
        const lngMax = bounds.getEast();
        const lngMin = bounds.getWest();

        const verticalLines = [];
        const horizontalLines = [];

        // Vertical lines
        for (let x = lngMin; x <= lngMax; x += spacing) {
            verticalLines.push([[latMin, x], [latMax, x]]);
        }

        // Horizontal lines
        for (let y = latMin; y <= latMax; y += spacing) {
            horizontalLines.push([[y, lngMin], [y, lngMax]]);
        }

        return (
            <>
                <Polyline
                    positions={verticalLines}
                    pathOptions={{ color, weight: 1, opacity: opacity, interactive: false }}
                />
                <Polyline
                    positions={horizontalLines}
                    pathOptions={{ color, weight: 1, opacity: opacity, interactive: false }}
                />
            </>
        );
    }

    return (
        <div style={{ display: 'flex', gap: '20px', fontFamily: 'sans-serif' }}>
            {/* MAP CONTAINER */}
            <div style={{ flex: 1, height: 'calc(100vh - 255px)', width: '100%', overflow: 'hidden', border: '1px solid #ddd' }}>
                <MapContainer crs={CRS.Simple} zoomSnap={0} style={{ height: '100%', width: '100%', background: '#f4f4f4' }}>
                    <ImageOverlay url={floorplanUrl} bounds={bounds} />
                    {/* 5m Grid (Red, thicker) */}
                    <GridLines bounds={bounds} spacing={5} show={show5m} color="#b60703" />
                    {/* 1m Grid (Gray, thinner) */}
                    <GridLines bounds={bounds} spacing={1} show={show1m} color="#b60703" opacity={0.4} />
                    {bounds && (
                        <Polygon
                            key="floorplan-outer-boundary"
                            positions={[
                                [bounds.getSouth(), bounds.getWest()],
                                [bounds.getNorth(), bounds.getWest()],
                                [bounds.getNorth(), bounds.getEast()],
                                [bounds.getSouth(), bounds.getEast()]
                            ]}
                            pathOptions={{
                                color: '#3388ff',
                                weight: 3,
                                fillColor: '#3388ff',
                                fillOpacity: 0.15
                            }}
                        />
                    )}
                    {zones.map((zone) => (
                        <Polygon key={zone.id} positions={zone.coords} pathOptions={{ color: '#b70b07', weight: 1, fillColor: '#b70b07', fillOpacity: 0.25 }}>
                            <Tooltip sticky>{zone.name}</Tooltip>
                        </Polygon>
                    ))}
                    {processedMarkers.map((marker) => (
                        <SafeCircleMarker key={marker.key} center={marker.pos} radius={4} pathOptions={{ fillColor: marker.color, fillOpacity: 1, color: marker.color, weight: 0.5 }} tooltip={marker.serialNumber} />
                    ))}
                    {processedPaths.map((path) => (
                        <React.Fragment key={`group-${path.name}`}>
                            <Polyline
                                positions={path.points}
                                pathOptions={{ color: path.color, weight: 1.5, opacity: 1 }}
                            />

                            {/* START MARKER */}
                            {path.startPoint && (
                                <SafeCircleMarker
                                    center={path.startPoint}
                                    radius={8}
                                    pathOptions={{ fillColor: path.color, fillOpacity: 1, color: path.color, weight: 2 }}
                                    tooltip={`Start: ${path.name}`}
                                />
                            )}

                            {/* END MARKER */}
                            {path.endPoint && (
                                <SafeCircleMarker
                                    center={path.endPoint}
                                    radius={10}
                                    pathOptions={{ fillColor: 'transparent', color: path.color, weight: 4 }}
                                    tooltip={`Current: ${path.name}`}
                                />
                            )}
                        </React.Fragment>
                    ))}

                    {hoverPoint && <SafeCircleMarker center={hoverPoint} radius={8} pathOptions={{ color: '#000', fillColor: '#d9ca26', fillOpacity: 1, weight: 2 }} />}
                    <MapController bounds={bounds} />
                </MapContainer>
            </div>
            {/* UI Controls */}
            <div className='grid-controls'>
                <div className='grid-controls-text'>
                    5m Grid:&nbsp;&nbsp; <Switch onValueChanged={toggleGrid5} />
                </div>
                <div className='grid-controls-text'>
                    1m Grid:&nbsp;&nbsp; <Switch onValueChanged={toggleGrid1} />
                </div>
            </div>

            {/* LEGEND SIDEBAR */}
            <div className='legend-asset'>
                Legend: &nbsp;
                {assetLegend.map((asset, index) => (
                    <div key={asset.name} style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', fontSize: '14px', color: asset.color }}>
                        <span>
                            {asset.name}
                            {/* Only render the comma and space if this is NOT the last item */}
                            {index < assetLegend.length - 1 && <>, &nbsp;</>}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
}